// apps/spa/src/components/EmbedEditor/ColorPicker.tsx
// HEX text input + 8 BASTN/Discord palette swatches; controlled via RHF
// useController. Validation that the typed hex is well-formed is owned by the
// parent form's zodResolver(embedSchema) — ColorPicker just renders the value
// and surfaces fieldState.error inline.
//
// Palette per RESEARCH OQ #3:
//   #d4af37 BASTN_GOLD, #f1cf72 gold-soft, #7f96b4 accent-2, #5e7390 accent-3,
//   #3ba55d Discord green, #f04747 Discord red, #faa61a Discord yellow,
//   #5865f2 Discord blurple.

import { useController } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const PALETTE = [
  '#d4af37', // BASTN_GOLD
  '#f1cf72', // gold-soft
  '#7f96b4', // accent-2
  '#5e7390', // accent-3
  '#3ba55d', // Discord green
  '#f04747', // Discord red
  '#faa61a', // Discord yellow
  '#5865f2', // Discord blurple
] as const;

export interface ColorPickerProps {
  name: string;
}

export function ColorPicker({ name }: ColorPickerProps) {
  const { field, fieldState } = useController({ name });
  // Defensively coerce non-string field values (e.g., when the parent form
  // stores color as an int and forgets to convert at the boundary). The
  // canonical 03-03 boundary stores hex; this guard prevents crashes during
  // mount races / mid-edit transitions.
  const rawValue = field.value;
  const value =
    typeof rawValue === 'string' && rawValue.length > 0 ? rawValue : '#d4af37';

  return (
    <div className="space-y-2">
      <Input
        type="text"
        value={value}
        onChange={(e) => field.onChange(e.target.value)}
        onBlur={field.onBlur}
        placeholder="#RRGGBB"
        aria-invalid={Boolean(fieldState.error)}
        data-slot="color-picker-input"
      />
      <div className="flex gap-2 flex-wrap" data-slot="color-picker-swatches">
        {PALETTE.map((hex) => (
          <button
            key={hex}
            type="button"
            aria-label={`Set color to ${hex}`}
            onClick={() => field.onChange(hex)}
            className={cn(
              'h-7 w-7 rounded-full border border-border transition-shadow',
              value.toLowerCase() === hex &&
                'ring-2 ring-primary ring-offset-2 ring-offset-card',
            )}
            style={{ backgroundColor: hex }}
            data-color={hex}
          />
        ))}
      </div>
      {fieldState.error?.message && (
        <p className="text-xs text-destructive" role="alert">
          {fieldState.error.message}
        </p>
      )}
    </div>
  );
}
